import type {
  ILoaderConfig,
  IOptionalParams,
  IStoreOption,
} from 'src/utils/loader'
import { ref } from 'vue'
import type { CoreError } from '../interfaces/common'
import { getLoader } from '../service/loaderFactory'
import { useAppStore } from 'src/stores'

export const useLoader = <T = any>(service: string, config: ILoaderConfig) => {
  const isLoading = ref(false)
  const error = ref<CoreError | null>(null)
  const result = ref<T>()
  const resultIndex = ref()
  const responseStatus = ref<number | undefined>()
  const deleteBulkResult = ref()
  const loader = getLoader(service)
  const store = useAppStore()

  if (!loader) {
    isLoading.value = false
    throw new Error('messagesGroup.internalError')
  }

  const useFetchFileLoader = async <T>(opt?: IOptionalParams) => {
    store.setLoading(false)
    isLoading.value = true
    const { data } = await loader.getAll<T>(config, {
      ...opt,
      responseType: 'blob',
    })
    isLoading.value = false
    store.setLoading(false)
    return data as any
  }

  const useFetchLoader = async <T>(opt?: IOptionalParams) => {
    store.setLoading(false)
    isLoading.value = true

    const { data, errorData } = await loader.getAll<T>(config, opt)
    error.value = errorData
    result.value = data as any
    isLoading.value = false
    store.setLoading(false)
    return result.value
  }

  const fetchByPageLoader = async <T>(opt?: IOptionalParams) => {
    store.setLoading(false)
    isLoading.value = true
    const { data, errorData } = await loader.getByPage<T>(config, opt)
    error.value = errorData
    result.value = data as any
    isLoading.value = false
    store.setLoading(false)
    return result.value
  }

  const getByIdLoader = async <T>(
    id: string,
    opt?: IOptionalParams,
    storeOpts?: IStoreOption,
  ) => {
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(true)
    isLoading.value = true
    const { data, errorData } = await loader.getById<T>(config, id, opt)
    error.value = errorData
    result.value = data as any
    isLoading.value = false
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(false)
    return result.value
  }

  const usePostLoader = async <T>(
    payload: any,
    opt?: IOptionalParams,
    storeOpts?: IStoreOption,
  ) => {
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(true)
    isLoading.value = true
    const { data, errorData, status } = await loader.post<T>(config, payload, opt)
    error.value = errorData
    result.value = data as any
    responseStatus.value = status
    isLoading.value = false
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(false)
    return result.value
  }

  const usePutLoader = async <T>(
    id: string,
    payload: any,
    custom = false,
    opt?: IOptionalParams,
  ) => {
    store.setLoading(true)
    isLoading.value = true
    const { data, errorData, status } = await loader.putById<T>(
      config,
      id,
      payload,
      custom,
      opt,
    )
    error.value = errorData
    result.value = data as any
    responseStatus.value = status
    isLoading.value = false
    store.setLoading(false)
    return result.value
  }

  const usePatchLoader = async <T>(
    id: string,
    payload: any,
    opt?: IOptionalParams,
  ) => {
    store.setLoading(true)
    isLoading.value = true
    const { data, errorData } = await loader.patch<T>(config, id, payload, opt)
    error.value = errorData
    result.value = data as any
    isLoading.value = false
    store.setLoading(false)
    return result.value
  }

  const useDeleteLoader = async <T>(
    id: string,
    opt?: IOptionalParams,
    storeOpts?: IStoreOption,
  ) => {
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(true)
    isLoading.value = true
    const { data, errorData } = await loader.delete<T>(config, id, opt)
    error.value = errorData
    result.value = data as any
    isLoading.value = false
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(false)
    return result.value
  }

  const useDeleteBulk = async <T>(
    payload: any,
    opt?: IOptionalParams,
    storeOpts?: IStoreOption,
  ) => {
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(true)
    isLoading.value = true
    const { data, errorData } = await loader.deleteBulk<T>(config, payload, opt)
    error.value = errorData
    deleteBulkResult.value = data as any
    isLoading.value = false
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(false)

    if (error.value) {
      return error.value
    }
    return deleteBulkResult.value
  }

  const usePutIndexLoader = async <T>(
    payload: any,
    opt?: IOptionalParams,
    storeOpts?: IStoreOption,
  ) => {
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(true)
    isLoading.value = true
    const { data, errorData } = await loader.putIndexing<T>(
      config,
      payload,
      opt,
    )
    error.value = errorData
    resultIndex.value = data as any
    isLoading.value = false
    storeOpts?.useGlobalLoadingIndicator && store.setLoading(false)
    return result.value
  }

  return {
    isLoading,
    result,
    deleteBulkResult,
    error,
    responseStatus,
    useFetchLoader,
    useFetchFileLoader,
    fetchByPageLoader,
    getByIdLoader,
    usePostLoader,
    usePutLoader,
    usePutIndexLoader,
    useDeleteLoader,
    useDeleteBulk,
    usePatchLoader,
  }
}
